import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { t, tCount } from '../../i18n/uiText';
import { isWebAuthenticated, jsonHeadersWithAuth } from '../../utils/webAuth';
import { requestAuthPopup } from '../../utils/authPopup';
import { resolveAiLimitMessage } from '../../utils/aiLimitMessage';

const API_BASE = import.meta.env.VITE_API_URL || 'https://verbum-api-production.up.railway.app/api';

const normalizeSearch = (value) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/\s+/g, ' ')
    .trim();

const parseSearchQuery = (query) => {
  const q = normalizeSearch(query);
  if (!q) return { raw: '', bookPart: '', chapter: null, chapterToken: '' };

  const chapterMatch = q.match(/(?:^|\s)(\d{1,3})$/);
  const chapter = chapterMatch ? parseInt(chapterMatch[1], 10) : null;
  const chapterToken = chapterMatch ? chapterMatch[1] : '';
  const bookPart = chapterMatch ? q.slice(0, chapterMatch.index).trim() : q;
  return { raw: q, bookPart, chapter: Number.isNaN(chapter) ? null : chapter, chapterToken };
};

const hasMatchingChapter = (maxChapter, chapterToken) => {
  if (!chapterToken) return true;
  for (let n = 1; n <= maxChapter; n += 1) {
    if (String(n).includes(chapterToken)) return true;
  }
  return false;
};

const toPositiveInt = (value, fallback = 0) => {
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? Math.floor(num) : fallback;
};

const renderHighlightedDigits = (value, token) => {
  const text = String(value);
  if (!token) return text;

  const parts = [];
  let cursor = 0;
  let key = 0;
  while (cursor < text.length) {
    const idx = text.indexOf(token, cursor);
    if (idx === -1) {
      parts.push(<span key={`t-${key++}`}>{text.slice(cursor)}</span>);
      break;
    }
    if (idx > cursor) {
      parts.push(<span key={`t-${key++}`}>{text.slice(cursor, idx)}</span>);
    }
    parts.push(
      <span key={`m-${key++}`} className="chapter-digit-match">
        {text.slice(idx, idx + token.length)}
      </span>
    );
    cursor = idx + token.length;
  }
  return parts;
};

function ChapterSelectorHeader({ searchInputRef, searchQuery, setSearchQuery, onClose }) {
  return (
    <div className="chapter-selector-content-header">
      <div className="chapter-selector-toolbar">
        <div className="chapter-selector-search-field">
          <span className="chapter-selector-search-icon" aria-hidden>
            🔍
          </span>
          <input
            ref={searchInputRef}
            type="search"
            enterKeyHint="search"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('chapterSelectorSearchPlaceholder')}
            className="chapter-selector-search-input"
            aria-label={t('chapterSelectorSearchLabel')}
          />
        </div>
        <button type="button" onClick={onClose} className="chapter-selector-close-btn" aria-label={t('close')}>
          ×
        </button>
      </div>
    </div>
  );
}

function DescriptionPanel({ interpretationLoading, interpretationError, interpretation, onRetry }) {
  const application = interpretation?.application;
  const principle =
    application && typeof application === 'object' ? (application.principle ?? application.Principle ?? '') : '';
  const modernContext =
    application && typeof application === 'object'
      ? (application.modernContext ?? application.modern_context ?? application.ModernContext ?? '')
      : '';

  return (
    <div className="chapter-selector-description">
      {interpretationLoading ? (
        <div className="flex flex-col items-center justify-center py-6">
          <div className="w-10 h-10 border-4 border-slate-200 border-t-slate-500 rounded-full animate-spin mb-3" />
          <p className="text-slate-500 font-medium">{t('loadingBookDescription')}</p>
        </div>
      ) : interpretationError ? (
        <div className="text-center py-6">
          <p className="text-red-500 font-medium mb-4">{t('error')}: {interpretationError}</p>
          <button type="button" onClick={onRetry} className="bible-ui-btn bible-ui-btn--primary">
            {t('retry')}
          </button>
        </div>
      ) : interpretation ? (
        <div className="space-y-5">
          {interpretation.meaning && (
            <div className="book-section">
              <h3 className="text-base font-bold text-slate-800 mb-2 flex items-center gap-2">
                <span>📜</span> {t('description')}
              </h3>
              <p className="text-slate-700 leading-relaxed text-sm mb-3">{interpretation.meaning.summary}</p>
            </div>
          )}

          {interpretation.context && (
            <div className="book-section">
              <h3 className="text-base font-bold text-slate-800 mb-2 flex items-center gap-2">
                <span>📖</span> {t('contextTab')}
              </h3>
              {interpretation.context.bookContext && (
                <p className="text-slate-700 leading-relaxed text-sm mb-2">{interpretation.context.bookContext}</p>
              )}
              {interpretation.context.biblicalContext && (
                <p className="text-slate-600 leading-relaxed text-sm">{interpretation.context.biblicalContext}</p>
              )}
            </div>
          )}

          {interpretation.theological && (
            <div className="book-section">
              <h3 className="text-base font-bold text-slate-800 mb-2 flex items-center gap-2">
                <span>✝️</span> {t('theology')}
              </h3>
              {interpretation.theological.themes?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {interpretation.theological.themes.map((theme, idx) => (
                    <span key={idx} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs font-medium">
                      {theme}
                    </span>
                  ))}
                </div>
              )}
              {interpretation.theological.christConnection && (
                <div className="p-3 bg-gradient-to-r from-slate-100 to-white rounded-lg">
                  <p className="text-xs uppercase tracking-wider text-[#B08C3A] font-semibold mb-1">{t('christConnection')}</p>
                  <p className="text-slate-700 leading-relaxed text-sm">{interpretation.theological.christConnection}</p>
                </div>
              )}
            </div>
          )}

          {(interpretation.cultural?.hasContent || interpretation.historical?.hasContent) && (
            <div className="book-section">
              <h3 className="text-base font-bold text-slate-800 mb-2 flex items-center gap-2">
                <span>🏺</span> {t('culture')} {t('historical').toLowerCase()}
              </h3>
              {interpretation.cultural?.facts?.map((fact, idx) => (
                <p key={`c-${idx}`} className="text-slate-600 leading-relaxed text-sm mb-1">→ {fact}</p>
              ))}
              {interpretation.historical?.facts?.map((fact, idx) => (
                <p key={`h-${idx}`} className="text-slate-600 leading-relaxed text-sm mb-1">→ {fact}</p>
              ))}
            </div>
          )}

          {interpretation.application && (
            <div className="book-section p-3 bg-gradient-to-r from-green-50 to-white rounded-lg border border-green-100">
              <h3 className="text-base font-bold text-slate-800 mb-2 flex items-center gap-2">
                <span>💡</span> {t('application')}
              </h3>
              {typeof application === 'string' ? (
                <p className="text-slate-700 leading-relaxed text-sm">{application}</p>
              ) : (
                <>
                  {principle && <p className="text-slate-700 leading-relaxed text-sm mb-2">{principle}</p>}
                  {modernContext && <p className="text-slate-700 leading-relaxed text-sm">{modernContext}</p>}
                  {!principle && !modernContext && (
                    <p className="text-slate-500 leading-relaxed text-sm">{t('applicationEmpty')}</p>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default function ChapterSelector({
  bookId,
  currentChapter,
  totalChapters,
  bookName,
  books = [],
  onSelect,
  onClose,
  loginReturnPath = '/',
}) {
  const navigate = useNavigate();
  const [interpretation, setInterpretation] = useState(null);
  const [interpretationLoading, setInterpretationLoading] = useState(false);
  const [interpretationError, setInterpretationError] = useState(null);
  const [activeTab, setActiveTab] = useState('chapters');
  const currentBookRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const sheetContentRef = useRef(null);
  const searchInputRef = useRef(null);
  const currentChapterNum = parseInt(currentChapter, 10);
  const currentBookIdNum = parseInt(bookId, 10);
  const [searchQuery, setSearchQuery] = useState('');

  const [expandedBookId, setExpandedBookId] = useState(Number.isNaN(currentBookIdNum) ? null : currentBookIdNum);

  useEffect(() => {
    const parsedBookId = parseInt(bookId, 10);
    setExpandedBookId(Number.isNaN(parsedBookId) ? null : parsedBookId);
  }, [bookId]);

  useEffect(() => {
    // При смене раскрытой книги сбрасываем вкладку и описание
    setActiveTab('chapters');
    setInterpretation(null);
    setInterpretationError(null);
  }, [expandedBookId]);

  const loadInterpretation = useCallback(
    async (bookIdToLoad) => {
      setInterpretationLoading(true);
      setInterpretationError(null);
      const language = localStorage.getItem('bibleLanguage') || 'en';
      const candidate = bookIdToLoad ?? expandedBookId ?? currentBookIdNum;
      const normalizedBookId = parseInt(candidate, 10);
      if (candidate == null || Number.isNaN(normalizedBookId) || normalizedBookId <= 0) {
        setInterpretationError(t('chapterSelectorDescriptionBookMissing'));
        setInterpretationLoading(false);
        return;
      }
      if (!isWebAuthenticated()) {
        setInterpretationLoading(false);
        requestAuthPopup(loginReturnPath, true);
        return;
      }
      try {
        const response = await fetch(`${API_BASE}/interpret`, {
          method: 'POST',
          headers: jsonHeadersWithAuth({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({ bookId: normalizedBookId, language }),
        });
        if (response.status === 401) {
          requestAuthPopup(loginReturnPath, true);
          throw new Error(t('signInRequiredForAi'));
        }
        if (response.status === 429) {
          const payload = await response.json().catch(() => null);
          if (payload?.code === 'DAILY_AI_LIMIT_REACHED') {
            throw new Error(resolveAiLimitMessage(payload, t, 'interpretation'));
          }
        }
        if (!response.ok) throw new Error(t('error'));
        const data = await response.json();
        setInterpretation(data);
      } catch (err) {
        setInterpretationError(err.message);
      } finally {
        setInterpretationLoading(false);
      }
    },
    [expandedBookId, currentBookIdNum, navigate, loginReturnPath]
  );

  useEffect(() => {
    // Загружаем описание только при явном выборе вкладки "Описание"
    if (activeTab !== 'description') return;
    if (expandedBookId == null || Number.isNaN(expandedBookId)) return;
    loadInterpretation(expandedBookId);
  }, [activeTab, expandedBookId, loadInterpretation]);

  useEffect(() => {
    if (expandedBookId == null) return;

    const scrollExpandedBookIntoView = () => {
      const block = currentBookRef.current;
      const container = scrollContainerRef.current;
      if (!block || !container) return;
      const prefersReduced =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const pad = 6;
      const cRect = container.getBoundingClientRect();
      const bRect = block.getBoundingClientRect();
      const delta = bRect.top - cRect.top - pad;
      if (Math.abs(delta) < 3) return;
      container.scrollTo({
        top: container.scrollTop + delta,
        behavior: prefersReduced ? 'auto' : 'smooth',
      });
    };

    const timer = window.setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(scrollExpandedBookIntoView);
      });
    }, 100);
    return () => window.clearTimeout(timer);
  }, [expandedBookId, books.length]);

  // iOS Safari: клавиатура уменьшает visualViewport — подстраиваем высоту листа, иначе контент «уезжает».
  useEffect(() => {
    const node = sheetContentRef.current;
    if (!node || typeof window === 'undefined') return;
    const vv = window.visualViewport;
    if (!vv) return;

    const apply = () => {
      // Лист привязан к верху: высота ≈ видимая область (клавиатура уже уменьшила visualViewport).
      const pad = 16;
      const cap = Math.max(220, Math.round(vv.height - pad));
      node.style.maxHeight = `${cap}px`;
    };

    apply();
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    return () => {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
      node.style.maxHeight = '';
    };
  }, []);

  // При вводе поиска не оставляем список прокрученным вниз (кажется, что «всё пропало»).
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTop = 0;
    });
  }, [searchQuery]);

  useEffect(() => {
    const onKeyDown = (e) => {
      // Если фокус уже в поле ввода, не перехватываем
      if (document.activeElement === searchInputRef.current) return;

      // Быстрая очистка поиска
      if (e.key === 'Escape' && searchQuery.trim()) {
        setSearchQuery('');
        return;
      }

      // Открываем поиск и начинаем ввод сразу с клавиатуры
      const isPrintableChar =
        e.key.length === 1 &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey;

      if (isPrintableChar) {
        setSearchQuery((prev) => prev + e.key);
        window.requestAnimationFrame(() => searchInputRef.current?.focus());
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [searchQuery]);

  const allBooks = useMemo(
    () => (books && books.length > 0 ? books : [{ id: currentBookIdNum, name: bookName, chapters: totalChapters }]),
    [books, currentBookIdNum, bookName, totalChapters]
  );
  const hasLoadedBooks = Array.isArray(books) && books.length > 0;

  const parsedSearch = useMemo(() => parseSearchQuery(searchQuery), [searchQuery]);

  const filteredBooks = useMemo(() => {
    if (!parsedSearch.raw) return allBooks;

    // Пока реальный список книг не подгрузился, не скрываем fallback-элемент,
    // чтобы попап не выглядел "пустым" при вводе на старте.
    if (!hasLoadedBooks) return allBooks;

    return allBooks.filter((book) => {
      const countNum = Number(book.chapters ?? book.totalChapters ?? totalChapters ?? 0);
      const safeCount = Number.isFinite(countNum) && countNum > 0 ? Math.floor(countNum) : 0;
      if (!hasMatchingChapter(safeCount, parsedSearch.chapterToken)) {
        return false;
      }

      if (!parsedSearch.bookPart) return true;
      const haystack = normalizeSearch(`${book.name} ${book.nameShort ?? ''} ${book.id}`);
      const compactHaystack = haystack.replace(/\s+/g, '');
      const compactNeedle = parsedSearch.bookPart.replace(/\s+/g, '');

      return haystack.includes(parsedSearch.bookPart) || compactHaystack.includes(compactNeedle);
    });
  }, [allBooks, parsedSearch, totalChapters, hasLoadedBooks]);

  useEffect(() => {
    // Без поиска — открываем текущую книгу по умолчанию
    if (!parsedSearch.raw) {
      if (Number.isFinite(currentBookIdNum) && currentBookIdNum > 0) {
        setExpandedBookId(currentBookIdNum);
      }
      return;
    }

    // Идёт только текстовый поиск книги: держим список свернутым
    if (!parsedSearch.chapterToken) {
      setExpandedBookId(null);
      return;
    }

    // Поиск включает номер главы: раскрываем первую подходящую книгу
    if (filteredBooks.length > 0) {
      const firstId = parseInt(filteredBooks[0]?.id, 10);
      if (Number.isFinite(firstId) && firstId > 0) {
        setExpandedBookId(firstId);
      }
    }
  }, [parsedSearch.raw, parsedSearch.chapterToken, filteredBooks, currentBookIdNum]);

  return (
    <div className="chapter-selector-modal" onClick={onClose}>
      <div
        ref={sheetContentRef}
        className="chapter-selector-content chapter-selector-content--fixed"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bible-modal-sheet-handle" aria-hidden="true" />
        <ChapterSelectorHeader
          searchInputRef={searchInputRef}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          onClose={onClose}
        />

        <div className="chapter-selector-scroll" ref={scrollContainerRef}>
        <div className="chapter-selector-book-stack">
          {filteredBooks.map((book) => {
            const bId = parseInt(book.id, 10);
            const isOpen = Number.isFinite(bId) && bId === expandedBookId;
            const safeCount = toPositiveInt(book.chapters ?? book.totalChapters ?? totalChapters ?? 0, 0);
            const chapterFilterToken = parsedSearch.chapterToken;

            return (
              <div
                key={book.id}
                ref={bId === expandedBookId ? currentBookRef : null}
                className="chapter-selector-book-card"
              >
                <button
                  type="button"
                  className="chapter-selector-book-row"
                  onClick={() => {
                    if (!Number.isFinite(bId) || bId <= 0) return;
                    setExpandedBookId((prev) => (prev === bId ? null : bId));
                  }}
                  aria-expanded={isOpen}
                >
                  <div className="chapter-selector-book-row-title">
                    <span className="chapter-selector-book-row-icon" aria-hidden>
                      📘
                    </span>
                    <span className="chapter-selector-book-row-name">{book.name}</span>
                  </div>
                  <div className="chapter-selector-book-row-meta">
                    <span className="chapter-selector-book-row-count">
                      {tCount('chapterCountCompact', safeCount)}
                    </span>
                    <span
                      className={`chapter-selector-accordion-chevron ${isOpen ? 'is-open' : ''}`}
                      aria-hidden
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </span>
                  </div>
                </button>

                {isOpen && (
                  <div className="chapter-selector-book-panel">
                    <div className="chapter-selector-segmented" role="tablist" aria-label={t('bookSectionLabel')}>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'chapters'}
                        className={`chapter-selector-segment ${activeTab === 'chapters' ? 'active' : ''}`}
                        onClick={() => setActiveTab('chapters')}
                      >
                        {t('chapters')}
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'description'}
                        className={`chapter-selector-segment ${activeTab === 'description' ? 'active' : ''}`}
                        onClick={() => setActiveTab('description')}
                      >
                        {t('description')}
                      </button>
                    </div>

                    {activeTab === 'chapters' && (
                      <div className="chapter-selector-grid">
                        {Array.from({ length: safeCount }, (_, i) => i + 1)
                          .filter((chapterNum) => !chapterFilterToken || String(chapterNum).includes(chapterFilterToken))
                          .map((chapterNum) => {
                            const isCurrent = bId === currentBookIdNum && chapterNum === currentChapterNum;
                            const isSearchMatch = !!chapterFilterToken && String(chapterNum).includes(chapterFilterToken);
                            return (
                              <button
                                key={`${book.id}-${chapterNum}`}
                                onClick={() => onSelect(bId, chapterNum)}
                                className={`chapter-selector-button ${isCurrent ? 'active' : ''} ${isSearchMatch ? 'matching-search' : ''}`}
                              >
                                {renderHighlightedDigits(chapterNum, chapterFilterToken)}
                              </button>
                            );
                          })}
                      </div>
                    )}

                    {activeTab === 'description' && (
                      <DescriptionPanel
                        interpretationLoading={interpretationLoading}
                        interpretationError={interpretationError}
                        interpretation={interpretation}
                        onRetry={() => loadInterpretation(bId)}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {filteredBooks.length === 0 && (
            <div className="chapter-selector-empty-hint">
              {t('chapterSelectorNothingFound')}
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}

